import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Box,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import config from '@configFile';
import { useGetUserData } from '@hooks/useGetUserData';
import trackUse from '@utils/trackUse';
import { VariationType } from '@types';
import { MonetizingLink } from '@components/index';
import { getProductLink, logPostHogEvent } from '@utils/index';
import AddVariation from '@components/AdminActions/AddVariation';
import VariationPrice from './components/VariationPrice';

interface SortableTableProps {
  data: VariationType[];
}

enum Order {
  Ascending = 'asc',
  Descending = 'desc'
}

const VariationsTable: React.FC<SortableTableProps> = ({ data }) => {
  const { data: user } = useGetUserData();
  const [order, setOrder] = useState<Order>(Order.Ascending);
  const [orderBy, setOrderBy] = useState<keyof VariationType>('amount');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSortRequest = (property: keyof VariationType) => {
    const newOrder =
      orderBy === property && order === Order.Ascending
        ? Order.Descending
        : Order.Ascending;
    setOrder(newOrder);
    setOrderBy(property);

    trackUse({
      item: `variation-${property}-click`,
      value: newOrder,
      type: 'CLICK'
    });

    logPostHogEvent('variation-sort', {
      value: `${property} - ${newOrder}`,
      type: 'CLICK'
    });
  };

  const sortedData = [...data].sort((a, b) => {
    // eslint-disable-next-line no-param-reassign
    if (order === Order.Descending) [a, b] = [b, a];

    if (a[orderBy] < b[orderBy]) return -1;
    if (a[orderBy] > b[orderBy]) return 1;

    return 0;
  });

  const renderVariationImage = (product: VariationType) => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent={{ xs: 'center', sm: 'flex-start' }}
    >
      <MonetizingLink
        href={getProductLink(product.ASIN, config.AFFILIATE_TAGS.VARIATIONS)}
        target="_blank"
        rel="noopener"
        tag={config.AFFILIATE_TAGS.VARIATIONS}
        clickType="variation-image-click"
      >
        <img
          src={product.image?.replace('http:', 'https:')}
          alt={product.title}
          style={{ width: '100px', height: 'auto', borderRadius: '4px' }}
        />
      </MonetizingLink>
    </Box>
  );

  const renderVariationTitle = (product: VariationType) => (
    <MonetizingLink
      variant="body1"
      tag={config.AFFILIATE_TAGS.VARIATIONS}
      clickType="variation-click"
      href={getProductLink(product.ASIN, config.AFFILIATE_TAGS.VARIATIONS)}
      target="_blank"
      rel="noopener"
      underline="always"
    >
      {product.title}
    </MonetizingLink>
  );

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Typography variant="overline" color="text.secondary" sx={{ mb: 1 }}>
        {sortedData.length}{' '}
        {sortedData.length === 1 ? 'variation' : 'variations'}
      </Typography>
      <TableContainer component={Paper} elevation={3}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'amount'}
                  direction={order}
                  onClick={() => handleSortRequest('amount')}
                  IconComponent={ArrowUpwardIcon}
                >
                  Price
                </TableSortLabel>
              </TableCell>
              {!isMobile && (
                <>
                  <TableCell>Image</TableCell>
                  <TableCell>Title</TableCell>
                </>
              )}
              {isMobile && <TableCell>Information</TableCell>}
              {!!user?.isAdmin && <TableCell>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((product) => (
              <TableRow key={product.ASIN} hover>
                <TableCell>
                  <VariationPrice {...product} />
                </TableCell>
                {!isMobile && (
                  <>
                    <TableCell>{renderVariationImage(product)}</TableCell>
                    <TableCell>{renderVariationTitle(product)}</TableCell>
                  </>
                )}
                {isMobile && (
                  <TableCell>
                    {renderVariationImage(product)}
                    <Box sx={{ mt: 1 }}>{renderVariationTitle(product)}</Box>
                  </TableCell>
                )}
                {!!user?.isAdmin && (
                  <TableCell>
                    <AddVariation
                      ASIN={product.ASIN}
                      threshold={parseFloat((product.amount / 100).toFixed(2))}
                    />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default VariationsTable;
